import React, { useState } from 'react';
import {
    IconButton,
    Dialog,
    Typography,
    DialogTitle,
    DialogContent,
    Box,
    DialogActions,
    TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { LoadingButton } from '@mui/lab';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { BuyCreditsModalProps } from '../../../interfaces/Credits';
import { useGlobalData } from '../../../hooks/globalDataContext';
import { getConfig } from '../../../config/env';

const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({ open, onClose }) => {
    const url = getConfig().BASE_URL;
    const { t } = useTranslation();
    const { notifyCreditsUpdated } = useGlobalData();
    const authToken = sessionStorage.getItem('authToken');
    const [credits, setCredits] = useState<number>(10);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleIncrement = () => {
        setCredits(prev => prev + 1);
    };

    const handleDecrement = () => {
        if (credits > 10) {
            setCredits(prev => prev - 1);
        }
    };

    const handleCreditsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value) || 0;
        // Permette di inserire qualsiasi valore, anche temporaneamente inferiore a 10
        setCredits(value);
    };

    const handleBuyCredits = async () => {
        // Validazione prima di procedere con l'acquisto
        if (credits < 10) {
            // Reset al valore minimo se l'utente ha inserito un valore troppo basso
            setCredits(10);
            return;
        }

        setIsLoading(true);
        try {
            const checkoutSession = await axios.get(`${url}/billing/checkout/create-checkout-session/`, {
                headers: { Authorization: `Bearer ${authToken}` },
                params: { amount: credits }
            });
            const data = checkoutSession.data.checkout_url;

            // Notifica l'aggiornamento dei crediti
            notifyCreditsUpdated();

            window.open(data, '_blank');
        } catch (error) {
            console.error('Errore durante il pagamento:', error);
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog
            fullWidth
            open={open}
            maxWidth="sm"
            onClose={handleClose}
            PaperProps={{ style: { backgroundColor: '#ffffff' } }}
        >
            <DialogTitle>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A1A1A' }}>
                    {t('billing:buy_credits_title')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475467', mt: 1 }}>
                    {t('billing:buy_credits_description')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ maxWidth: '100%', overflowX: 'hidden' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <IconButton
                            onClick={handleDecrement}
                            disabled={credits <= 10}
                            sx={{
                                backgroundColor: '#F8F9FA',
                                border: '1px solid #DBDBDB',
                                '&:hover': {
                                    backgroundColor: '#E9ECEF'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#F8F9FA',
                                    opacity: 0.5
                                }
                            }}
                        >
                            <RemoveIcon />
                        </IconButton>

                        <TextField
                            type="number"
                            value={credits}
                            onChange={handleCreditsChange}
                            slotProps={{
                                htmlInput: {
                                    min: 10,
                                    max: 1000000,
                                    inputMode: 'numeric'
                                }
                            }}
                            sx={{
                                width: '200px',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#DBDBDB',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#DBDBDB',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#DBDBDB',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    textAlign: 'center',
                                    fontSize: '1.125rem',
                                    fontWeight: 'bold',
                                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '&[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                }
                            }}
                        />

                        <IconButton
                            onClick={handleIncrement}
                            sx={{
                                backgroundColor: '#F8F9FA',
                                border: '1px solid #DBDBDB',
                                '&:hover': {
                                    backgroundColor: '#E9ECEF'
                                }
                            }}
                        >
                            <AddIcon />
                        </IconButton>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ width: '100%' }}>
                    <LoadingButton
                        onClick={handleBuyCredits}
                        loading={isLoading}
                        disabled={credits < 10}
                        sx={{
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            backgroundColor: '#E9495A',
                            color: '#FFFFFF',
                            '&:hover': { backgroundColor: '#E6263B' },
                            '&.Mui-disabled': { backgroundColor: '#E9495A', opacity: 0.5 },
                            width: '100%'
                        }}
                    >
                        {t('billing:buy_credits_button')}
                    </LoadingButton>
                </Box>
                <Typography variant="body2" sx={{ color: '#475467', width: '100%', textAlign: 'left' }}>
                    {t('billing:buy_credits_stripe_warning')}
                </Typography>
            </DialogActions>
        </Dialog>
    );
};

export default BuyCreditsModal; 